import { Suspense, useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from '@/components/error-boundary';
import {
  Building2,
  Car,
  LogOut,
  Menu,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Sun,
  UserRound,
  Users,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { type AlertDto, type GlobalSearchDto, LIVE_EVENTS, ROLE_LABELS } from '@brimostech/shared';
import {
  Avatar,
  AvatarFallback,
  Button,
  cn,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  initials,
  Kbd,
  Sheet,
  SheetContent,
  Spinner,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@brimostech/ui';
import { Brand, BrandMark } from '@/components/brand';
import { ApiError } from '@/lib/api-client';
import { useLiveEvent } from '@/lib/live';
import { useCan } from '@/lib/permissions';
import { queries } from '@/lib/queries';
import { type Theme, useTheme } from '@/lib/theme-context';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useAuth, useCurrentUser } from '../auth/auth-context';
import { CondominiumSwitcher } from './condominium-switcher';
import { type NavGroup, type NavItem, visibleNavGroups, visibleNavItems } from './navigation';

const SIDEBAR_KEY = 'condominio.sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppShell() {
  const user = useCurrentUser();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const items = visibleNavItems(user);
  const groups = visibleNavGroups(user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canAlerts = useCan('alerts:read');
  const location = useLocation();

  // New alerts reach whoever is in the portal, on any page (5.10).
  useLiveEvent<AlertDto>(
    LIVE_EVENTS.alert,
    useCallback(
      (alert) => {
        void queryClient.invalidateQueries({ queryKey: ['alerts'] });
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        if (alert.status !== 'open') return;
        const show = alert.severity === 'critical' ? toast.error : toast.warning;
        show(alert.title, {
          description: alert.message,
          action: { label: 'Ver alertas', onClick: () => navigate('/alertas') },
        });
      },
      [queryClient, navigate],
    ),
    canAlerts && user.activeCondominiumId !== null,
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      try {
        localStorage.setItem(SIDEBAR_KEY, value ? '0' : '1');
      } catch {
        // ignore storage errors
      }
      return !value;
    });
  };

  return (
    <div className="flex min-h-svh bg-background">
      <aside
        className={cn(
          'sticky top-0 hidden h-svh shrink-0 flex-col border-r border-foreground/5 bg-sidebar text-sidebar-foreground transition-[width] duration-200 lg:flex dark:border-sidebar-border',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        <SidebarContent groups={groups} collapsed={collapsed} onToggle={toggleCollapsed} />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 bg-sidebar p-0 text-sidebar-foreground">
          <SheetHeader className="sr-only">
            <SheetTitle>Menu</SheetTitle>
            <SheetDescription>Navegação principal</SheetDescription>
          </SheetHeader>
          <SidebarContent
            groups={groups}
            collapsed={false}
            onNavigate={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-foreground/5 bg-background/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 sm:px-6 dark:border-border">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu />
          </Button>
          <ConnectedCondominiumSwitcher />
          <div className="ml-auto flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="hidden gap-2 text-muted-foreground sm:inline-flex"
              onClick={() => setPaletteOpen(true)}
            >
              <Search className="size-4" />
              <span>Buscar</span>
              <Kbd>⌘K</Kbd>
            </Button>
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          {/* Keyed by path: the entrance animation plays once per navigation, not on every render. */}
          <div
            key={location.pathname}
            className="reveal-stagger mx-auto w-full max-w-6xl space-y-6"
          >
            <ErrorBoundary scope="a página">
              <Suspense fallback={<PageFallback />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} items={items} />
    </div>
  );
}

function PageFallback() {
  return (
    <div className="flex min-h-64 items-center justify-center">
      <Spinner />
    </div>
  );
}

function SidebarContent({
  groups,
  collapsed,
  onToggle,
  onNavigate,
}: {
  groups: NavGroup[];
  collapsed: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
}) {
  const user = useCurrentUser();
  const roleLabel = user.activeRole ? ROLE_LABELS[user.activeRole] : 'Sem papel ativo';
  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          'flex h-14 items-center border-b border-sidebar-border px-3',
          collapsed ? 'justify-center' : 'justify-between',
        )}
      >
        {collapsed ? <BrandMark /> : <Brand />}
        {onToggle ? (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onToggle}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            className={cn(collapsed && 'hidden')}
          >
            <PanelLeftClose />
          </Button>
        ) : null}
      </div>
      {collapsed && onToggle ? (
        <div className="flex justify-center py-2">
          <Button variant="ghost" size="icon-sm" onClick={onToggle} aria-label="Expandir menu">
            <PanelLeftOpen />
          </Button>
        </div>
      ) : null}
      <nav className="flex-1 space-y-4 overflow-y-auto p-2 scrollbar-none" aria-label="Principal">
        {groups.map((group) => (
          <div key={group.label ?? 'main'} className="space-y-1">
            {group.label && !collapsed ? (
              <p className="px-3 pt-1 text-xs font-medium text-muted-foreground/80">
                {group.label}
              </p>
            ) : null}
            {group.items.map((item) => renderItem(item, collapsed, onNavigate))}
          </div>
        ))}
      </nav>
      <div className={cn('border-t border-sidebar-border p-3', collapsed && 'flex justify-center')}>
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarFallback className="bg-primary/10 text-primary">
              {initials(user.name)}
            </AvatarFallback>
          </Avatar>
          {collapsed ? null : (
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-medium">{user.name}</p>
              <p className="truncate text-xs text-muted-foreground">{roleLabel}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function renderItem(item: NavItem, collapsed: boolean, onNavigate?: () => void) {
  const link = (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
          isActive
            ? 'bg-primary/10 text-primary dark:bg-primary/15'
            : 'text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
          collapsed && 'justify-center px-0',
        )
      }
    >
      <item.icon className="size-4 shrink-0" />
      {collapsed ? <span className="sr-only">{item.label}</span> : <span>{item.label}</span>}
    </NavLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip key={item.to}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

function ConnectedCondominiumSwitcher() {
  const user = useCurrentUser();
  const { switchCondominium } = useAuth();
  return (
    <CondominiumSwitcher
      options={user.condominiums}
      currentId={user.activeCondominiumId}
      onSelect={(id) => {
        void switchCondominium(id)
          .then((next) => {
            const name = next.condominiums.find((c) => c.id === id)?.name ?? 'condomínio';
            toast.success(`Agora você está em ${name}.`);
          })
          .catch((error: unknown) => {
            toast.error(
              error instanceof ApiError ? error.message : 'Não foi possível trocar de condomínio.',
            );
          });
      }}
    />
  );
}

function ThemeToggle() {
  const { theme, resolved, setTheme } = useTheme();
  const Icon = resolved === 'dark' ? Moon : Sun;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Alterar tema">
          <Icon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
          <DropdownMenuRadioItem value="light">
            <Sun /> Claro
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon /> Escuro
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor /> Sistema
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const user = useCurrentUser();
  const { logout, logoutAll } = useAuth();
  const navigate = useNavigate();

  const run = (action: () => Promise<void>, message: string) => {
    void action()
      .then(() => {
        toast.success(message);
        navigate('/login', { replace: true });
      })
      .catch(() => navigate('/login', { replace: true }));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" aria-label="Menu do usuário">
          <Avatar>
            <AvatarFallback className="bg-primary/10 text-primary">
              {initials(user.name)}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-sm font-medium">{user.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user.email ?? user.phone}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/perfil')}>
          <UserRound /> Meu perfil e segurança
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => run(logout, 'Você saiu.')}>
          <LogOut /> Sair
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => run(logoutAll, 'Você saiu de todos os dispositivos.')}>
          <LogOut /> Sair de todos os dispositivos
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CommandPalette({
  open,
  onOpenChange,
  items,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: NavItem[];
}) {
  const user = useCurrentUser();
  const { switchCondominium } = useAuth();
  const navigate = useNavigate();
  const canSearch = useCan('registry:read');
  const [query, setQuery] = useState('');
  const term = useDebouncedValue(query.trim(), 200);
  const results = useQuery({
    ...queries.search(user.activeCondominiumId, term),
    enabled: open && canSearch && user.activeCondominiumId !== null && term.length >= 2,
    placeholderData: (previous: GlobalSearchDto | undefined) => previous,
  });
  const close = () => {
    onOpenChange(false);
    setQuery('');
  };
  const go = (to: string) => {
    navigate(to);
    close();
  };
  const found = term.length >= 2 ? results.data : undefined;

  return (
    <CommandDialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <CommandInput
        placeholder={
          canSearch
            ? 'Unidade, pessoa, placa, página ou condomínio'
            : 'Ir para... ou trocar de condomínio'
        }
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {results.isFetching && term.length >= 2 ? 'Buscando…' : 'Nada encontrado.'}
        </CommandEmpty>
        {found && found.units.length > 0 ? (
          <CommandGroup heading="Unidades">
            {found.units.map((unit) => (
              <CommandItem
                key={unit.id}
                value={`unidade ${unit.label} ${term}`}
                onSelect={() => go(`/unidades/${unit.id}`)}
              >
                <Building2 /> {unit.label}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {found && found.persons.length > 0 ? (
          <CommandGroup heading="Pessoas">
            {found.persons.map((person) => (
              <CommandItem
                key={person.id}
                value={`pessoa ${person.fullName} ${term}`}
                onSelect={() => go(`/pessoas/${person.id}`)}
              >
                <Users /> {person.fullName}
                {person.unitLabels.length > 0 ? (
                  <span className="ml-auto text-xs text-muted-foreground">
                    {person.unitLabels.join(', ')}
                  </span>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {found && found.vehicles.length > 0 ? (
          <CommandGroup heading="Veículos">
            {found.vehicles.map((vehicle) => (
              <CommandItem
                key={vehicle.id}
                value={`veiculo ${vehicle.plate} ${term}`}
                onSelect={() => go(`/veiculos?q=${encodeURIComponent(vehicle.plate)}`)}
              >
                <Car /> {vehicle.plate}
                <span className="ml-auto text-xs text-muted-foreground">
                  {[vehicle.ownerName, vehicle.unitLabel].filter(Boolean).join(' · ')}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        <CommandGroup heading="Navegação">
          {items.map((item) => (
            <CommandItem
              key={item.to}
              value={`ir ${item.label}`}
              onSelect={() => {
                navigate(item.to);
                close();
              }}
            >
              <item.icon /> {item.label}
            </CommandItem>
          ))}
        </CommandGroup>
        {user.condominiums.length > 1 ? (
          <CommandGroup heading="Condomínios">
            {user.condominiums.map((option) => (
              <CommandItem
                key={option.id}
                value={`condominio ${option.name} ${option.organizationName}`}
                onSelect={() => {
                  close();
                  if (option.id !== user.activeCondominiumId) {
                    void switchCondominium(option.id).then(() =>
                      toast.success(`Agora você está em ${option.name}.`),
                    );
                  }
                }}
              >
                {option.name}
                <span className="ml-auto text-xs text-muted-foreground">
                  {option.organizationName}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}
